import axios from 'axios';

// 根据环境配置API基础URL
const getBaseURL = () => {
    // 支持通过环境变量显式指定（例如本地调试后端）
    const envBaseURL = import.meta.env.VITE_API_BASE_URL
    if (envBaseURL) {
        return envBaseURL
    }

    if (typeof window === 'undefined') {
        return ''
    }

    // 开发环境默认直连本机 Flask（端口 5000）
    // 注意：仅在页面本身运行在 localhost/127.0.0.1 时启用，避免把“客户端 localhost”写进构建产物后导致线上请求失败。
    const hostname = window.location.hostname
    const isLoopbackHost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0'
    if (import.meta.env.DEV && isLoopbackHost) {
        return 'http://localhost:5000'
    }

    // 生产环境与其它情况：与前端同域
    return window.location.origin
}

// 重试配置
const RETRY_CONFIG = {
    maxRetries: 3,              // 最大重试次数
    retryDelay: 1000,          // 基础重试延迟（毫秒）
    retryDelayMultiplier: 2,   // 延迟倍增因子（指数退避）
    maxRetryDelay: 10000       // 最大重试延迟
};

// 判断是否应该重试
const shouldRetry = (error) => {
    // 不重试的情况
    if (!error.config) {
        return false;
    }

    // 如果已经达到最大重试次数
    const currentRetryCount = error.config.__retryCount || 0;
    if (currentRetryCount >= RETRY_CONFIG.maxRetries) {
        return false;
    }

    // 客户端错误不重试（400系列，除了408超时和429限流）
    if (error.response) {
        const status = error.response.status;
        // 不重试的状态码：400, 401, 403, 404, 422等客户端错误
        if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
            return false;
        }
    }

    // 以下情况需要重试：
    // 1. 网络错误（无响应）
    // 2. 超时错误
    // 3. 500系列服务器错误
    // 4. 408请求超时
    // 5. 429限流（稍后重试）
    return true;
};

// 计算重试延迟（指数退避）
const getRetryDelay = (retryCount) => {
    const delay = RETRY_CONFIG.retryDelay * Math.pow(RETRY_CONFIG.retryDelayMultiplier, retryCount);
    return Math.min(delay, RETRY_CONFIG.maxRetryDelay);
};

// 延迟函数
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const service = axios.create({
    baseURL: getBaseURL(),
    timeout: 60000  // 增加到60秒，适应代理检测的长时间
});

service.interceptors.request.use(
    config => {
        config.headers = config.headers || {}
        config.headers['X-Token'] = 'token'
        config.headers['X-Requested-With'] = 'XMLHttpRequest'
        // 初始化重试计数
        if (!config.__retryCount) {
            config.__retryCount = 0;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

service.interceptors.response.use(
    response => {
        const data = response.data;

        // 统一格式：{ success, code, message, data }
        if (data && Object.prototype.hasOwnProperty.call(data, 'success')) {
            if (!data.success) {
                const structuredError = {
                    message: data.message || '请求失败',
                    code: data.code,
                    status: response.status,
                    raw: response,
                };
                return Promise.reject(structuredError);
            }
            // 成功时直接返回 data 字段，方便前端使用
            return data.data;
        }

        // 兼容旧格式：如果有 error 字段则当作错误
        if (data && data.error) {
            const structuredError = {
                message: data.error,
                code: data.code,
                status: response.status,
                raw: response,
            };
            return Promise.reject(structuredError);
        }

        // 其它情况直接返回原始 data
        return data;
    },
    async error => {
        const originalRequest = error.config;

        // 判断是否应该重试
        if (shouldRetry(error)) {
            originalRequest.__retryCount = (originalRequest.__retryCount || 0) + 1;
            const retryCount = originalRequest.__retryCount;
            const retryDelay = getRetryDelay(retryCount - 1);

            console.warn(
                `API请求失败，${retryDelay}ms后进行第${retryCount}次重试...`,
                {
                    url: originalRequest.url,
                    method: originalRequest.method,
                    error: error.message,
                    retryCount: retryCount,
                    maxRetries: RETRY_CONFIG.maxRetries
                }
            );

            // 等待后重试
            await delay(retryDelay);

            // 重新发起请求
            return service(originalRequest);
        }

        // 不重试或达到最大重试次数，返回友好错误信息
        console.error('API请求最终失败:', {
            url: originalRequest?.url,
            method: originalRequest?.method,
            error: error.message,
            retryCount: originalRequest?.__retryCount || 0
        });

        let errorMessage = '网络错误或服务不可用';
        let errorCode = undefined;
        let statusCode = undefined;

        if (error.response) {
            // 服务器响应了错误状态码
            const status = error.response.status;
            statusCode = status;
            const responseData = error.response.data;
            
            // 优先处理统一格式 {success, code, message, data}
            if (responseData && responseData.message) {
                errorMessage = responseData.message;
                if (responseData.code) {
                    errorCode = responseData.code;
                }
            } 
            // 兼容旧格式 {error, code}
            else if (responseData && responseData.error) {
                errorMessage = responseData.error;
                if (responseData.code) {
                    errorCode = responseData.code;
                }
            } 
            // 根据HTTP状态码提供默认错误消息
            else if (status === 429) {
                errorMessage = '请求过于频繁，请稍后再试';
            } else if (status === 500) {
                errorMessage = '服务器内部错误，请稍后重试';
            } else if (status === 502) {
                errorMessage = '网关错误，请稍后重试';
            } else if (status === 503) {
                errorMessage = '服务暂时不可用，请稍后重试';
            } else if (status === 504) {
                errorMessage = '网关超时，请稍后重试';
            } else if (status >= 500) {
                errorMessage = '服务器错误，请稍后重试';
            }
        } else if (error.request) {
            // 请求已发出但没有收到响应
            errorMessage = '请求超时或服务器无响应，请检查网络连接';
        } else if (error.code === 'ECONNABORTED') {
            errorMessage = '请求超时，请稍后重试';
        } else if (error.message.includes('Network Error')) {
            errorMessage = '网络连接失败，请检查网络后重试';
        }

        const structuredError = {
            message: errorMessage,
            code: errorCode,
            status: statusCode,
            raw: error
        };

        return Promise.reject(structuredError);
    }
);

// IP检测相关API
export const ipDetectionAPI = {
    // 单个IP检测
    detectSingle: (ip) => {
        return service.post('/ip/detect', {ip})
    },

    // 批量IP检测
    detectBatch: (ipList) => {
        return service.post('/ip/batch', {ip_list: ipList})
    }
}

// 代理检测相关API
export const proxyCheckAPI = {
    // 批量代理检测
    checkBatch: (proxyList) => {
        return service.post('/proxy/check', {
            proxy_list: proxyList
        })
    },

    // 检查代理在TK的真实地址
    checkTkIpRegion: (proxyHost, proxyPort, username, password, proxyType = 'http') => {
        return service.post('/proxy/check_tk_ip_region', {
            proxy_host: proxyHost,
            proxy_port: proxyPort,
            username: username,
            password: password,
            proxy_type: proxyType
        })
    }
}

// 邮件相关API
export const mailAPI = {
    // 获取验证码
    checkCode: (mailType, mailConfig, checkNum = 10, exclude = ["2024", "2025"]) => {
        return service.post('/mail/check-code', {
            mail_type: mailType,
            mail_config: mailConfig,
            check_num: checkNum,
            exclude: exclude
        })
    },

    // 获取邮件列表
    fetchEmails: (mailType, mailConfig, fetchNum = 10, mailbox = 'inbox') => {
        return service.post('/mail/fetch-emails', {
            mail_type: mailType,
            mail_config: mailConfig,
            fetch_num: fetchNum,
            mailbox: mailbox
        })
    }
}


export default service;
